import type {
  SessionResultsCacheModule,
  SessionResultsSnapshot,
} from "./types";

// Factory for a module-scope single-flight cache of the Progress
// session-results snapshot (perf B2). The shared `useSessionResults`
// hook is platform-agnostic, so the host injects an instance: web-rnw
// instantiates one of these at module scope; mobile passes its OWN
// equivalent module instead (so the save path can keep invalidating the
// same singleton without dragging AsyncStorage into useSaveWorkout's jest).
//
// Pure — no React, no storage. Concurrent callers (the panels mount
// together) await the same promise; a warm cache resolves immediately.
export function createSessionResultsCache(): SessionResultsCacheModule & {
  invalidate: () => void;
} {
  let cached: SessionResultsSnapshot | null = null;
  let inFlight: Promise<SessionResultsSnapshot> | null = null;
  const subscribers = new Set<() => void>();

  return {
    getCached: () => cached,
    ensure: (fetcher) => {
      if (cached) return Promise.resolve(cached);
      if (!inFlight) {
        inFlight = fetcher()
          .then((snap) => {
            cached = snap;
            inFlight = null;
            return snap;
          })
          .catch((e) => {
            inFlight = null;
            throw e;
          });
      }
      return inFlight;
    },
    subscribe: (fn) => {
      subscribers.add(fn);
      return () => {
        subscribers.delete(fn);
      };
    },
    // Drop the cache + notify mounted panels to re-fetch. Called from the
    // save path (a new workout recomputes its SessionResult server-side).
    invalidate: () => {
      cached = null;
      inFlight = null;
      for (const fn of subscribers) fn();
    },
  };
}
